// src/components/Visualization/MetricTable.js
import React, { useState, useMemo } from 'react';
import { 
  ChevronUp, ChevronDown, ArrowUp, 
  ArrowDown, Minus, Filter, Download, 
  Eye, Search, SlidersHorizontal, Trash
} from 'lucide-react';

/**
 * MetricTable - Tabela avançada para exibição de métricas e dados com formatação condicional
 * 
 * @param {Object} props
 * @param {Array} props.data - Dados para a tabela
 * @param {Array} props.columns - Configuração das colunas
 * @param {string} props.title - Título da tabela
 * @param {boolean} props.enableSorting - Habilitar ordenação das colunas
 * @param {boolean} props.enableFiltering - Habilitar filtragem dos dados
 * @param {Object} props.pagination - Configuração da paginação
 * @param {Function} props.onRowClick - Função a executar ao clicar em uma linha
 * @param {Function} props.onSelectionChange - Função a executar ao mudar a seleção
 * @param {Object} props.conditionalStyles - Estilos condicionais para as linhas
 * @param {boolean} props.enableSelection - Habilitar seleção de linhas
 * @param {Array} props.selectedRows - IDs das linhas selecionadas
 * @param {string} props.idField - Campo de ID único para cada linha
 * @param {Array} props.actions - Ações para cada linha (editar, excluir, etc.)
 */
const MetricTable = ({
  data = [],
  columns = [],
  title = '',
  enableSorting = true,
  enableFiltering = true,
  pagination = { enabled: false, pageSize: 10 },
  onRowClick,
  onSelectionChange,
  conditionalStyles,
  enableSelection = false,
  selectedRows = [],
  idField = 'id',
  actions = []
}) => {
  // Estados para controle da tabela
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleFilters, setVisibleFilters] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [localSelectedRows, setLocalSelectedRows] = useState(selectedRows || []);
  
  // Processar, filtrar, ordenar e paginar os dados
  const processedData = useMemo(() => {
    if (!data || !data.length) return [];
    
    // Aplicar pesquisa global
    let filteredData = [...data];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filteredData = filteredData.filter(row => {
        // Buscar em todas as colunas visíveis
        return columns.some(col => {
          if (col.hidden) return false;
          
          const value = row[col.key];
          if (value === undefined || value === null) return false;
          
          return String(value).toLowerCase().includes(term);
        });
      });
    }
    
    // Aplicar filtros por coluna
    Object.keys(filters).forEach(key => {
      const filterValue = filters[key];
      if (!filterValue || filterValue === 'all') return;
      
      filteredData = filteredData.filter(row => {
        const value = row[key];
        
        // Aplicar filtro com base no tipo
        const column = columns.find(col => col.key === key);
        
        if (column?.filterType === 'range') {
          const [min, max] = filterValue;
          return value >= min && value <= max;
        } else if (column?.filterType === 'select') {
          return String(value) === String(filterValue);
        } else {
          return String(value).toLowerCase().includes(filterValue.toLowerCase());
        }
      });
    });
    
    // Aplicar ordenação
    if (sortConfig.key) {
      filteredData.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        // Ordenar com base no tipo de dado
        const column = columns.find(col => col.key === sortConfig.key);
        
        if (column?.type === 'number') {
          return sortConfig.direction === 'asc' 
            ? Number(aValue || 0) - Number(bValue || 0)
            : Number(bValue || 0) - Number(aValue || 0);
        } else if (column?.type === 'date') {
          const dateA = aValue ? new Date(aValue).getTime() : 0;
          const dateB = bValue ? new Date(bValue).getTime() : 0;
          return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
        } else {
          const strA = String(aValue || '').toLowerCase();
          const strB = String(bValue || '').toLowerCase();
          return sortConfig.direction === 'asc' 
            ? strA.localeCompare(strB)
            : strB.localeCompare(strA);
        }
      });
    }
    
    // Aplicar paginação
    if (pagination.enabled) {
      const pageSize = pagination.pageSize || 10;
      const startIndex = (currentPage - 1) * pageSize;
      filteredData = filteredData.slice(startIndex, startIndex + pageSize);
    }
    
    return filteredData;
  }, [data, columns, sortConfig, filters, currentPage, searchTerm, pagination]);
  
  // Total de páginas para paginação
  const totalPages = useMemo(() => {
    if (!pagination.enabled || !data.length) return 1;
    
    // Aplicar filtros para calcular o total real de registros
    let filteredCount = data.length;
    
    // Aplicar pesquisa global
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filteredCount = data.filter(row => {
        return columns.some(col => {
          if (col.hidden) return false;
          const value = row[col.key];
          if (value === undefined || value === null) return false;
          return String(value).toLowerCase().includes(term);
        });
      }).length;
    }
    
    // Aplicar filtros por coluna
    Object.keys(filters).forEach(key => {
      const filterValue = filters[key];
      if (!filterValue || filterValue === 'all') return;
      
      filteredCount = data.filter(row => {
        const value = row[key];
        const column = columns.find(col => col.key === key);
        
        if (column?.filterType === 'range') {
          const [min, max] = filterValue;
          return value >= min && value <= max;
        } else if (column?.filterType === 'select') {
          return String(value) === String(filterValue);
        } else {
          return String(value).toLowerCase().includes(filterValue.toLowerCase());
        }
      }).length;
    });
    
    return Math.ceil(filteredCount / pagination.pageSize);
  }, [data, pagination, searchTerm, filters, columns]);
  
  // Manipuladores de eventos
  const handleSort = (key) => {
    if (!enableSorting) return;
    
    setSortConfig(prevSort => {
      if (prevSort.key === key) {
        // Inverter direção se já estiver ordenando por esta coluna
        return {
          key,
          direction: prevSort.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      
      // Nova coluna, ordenar ascendente por padrão
      return { key, direction: 'asc' };
    });
  };
  
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Voltar para primeira página ao pesquisar
  };
  
  const handleFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Voltar para primeira página ao filtrar
  };
  
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  const toggleRowExpansion = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  const handleRowSelection = (id) => {
    if (!enableSelection) return;
    
    setLocalSelectedRows(prev => {
      const newSelection = prev.includes(id)
        ? prev.filter(rowId => rowId !== id)
        : [...prev, id];
      
      // Notificar mudança de seleção
      if (onSelectionChange) {
        onSelectionChange(newSelection);
      }
      
      return newSelection;
    });
  };
  
  const handleSelectAll = (e) => {
    if (!enableSelection) return;
    
    const allSelected = processedData.length === localSelectedRows.length;
    
    const newSelection = allSelected
      ? [] // Desmarcar todos
      : processedData.map(row => row[idField]); // Selecionar todos
    
    setLocalSelectedRows(newSelection);
    
    // Notificar mudança de seleção
    if (onSelectionChange) {
      onSelectionChange(newSelection);
    }
  };
  
  // Renderizar cabeçalho da coluna
  const renderColumnHeader = (column) => {
    if (column.hidden) return null;
    
    // Verificar se esta coluna está sendo ordenada
    const isSorted = sortConfig.key === column.key;
    const sortDirection = isSorted ? sortConfig.direction : null;
    
    return (
      <th 
        key={column.key} 
        className={`px-4 py-3 ${enableSorting && column.sortable !== false ? 'cursor-pointer' : ''} ${column.headerClassName || ''}`}
        onClick={() => enableSorting && column.sortable !== false && handleSort(column.key)}
      >
        <div className="flex items-center justify-between">
          <span>{column.label}</span>
          
          {enableSorting && column.sortable !== false && (
            <span className="ml-2">
              {isSorted ? (
                sortDirection === 'asc' ? (
                  <ChevronUp size={16} className="text-blue-600" />
                ) : (
                  <ChevronDown size={16} className="text-blue-600" />
                )
              ) : (
                <ChevronDown size={16} className="text-gray-400 opacity-50" />
              )}
            </span>
          )}
        </div>
      </th>
    );
  };
  
  // Renderizar célula de uma coluna
  const renderCell = (row, column) => {
    if (column.hidden) return null;
    
    // Obter valor da célula
    let value = row[column.key];
    
    // Aplicar formatação se existir
    if (column.format && typeof column.format === 'function') {
      value = column.format(value, row);
    }
    
    // Aplicar renderização customizada se existir
    if (column.render && typeof column.render === 'function') {
      return (
        <td 
          key={column.key} 
          className={`px-4 py-3 ${column.cellClassName || ''}`}
        >
          {column.render(value, row)}
        </td>
      );
    }
    
    // Renderização padrão com base no tipo
    let cellContent;
    
    if (column.type === 'number') {
      // Estilo para valores numéricos baseados em regras
      let textColorClass = '';
      
      if (column.positiveNegative) {
        textColorClass = value > 0 ? 'text-green-600' : value < 0 ? 'text-red-600' : '';
      }
      
      // Se existe threshold, aplicar estilos
      if (column.threshold !== undefined) {
        if (column.thresholdInverse) {
          textColorClass = value < column.threshold ? 'text-green-600' : 'text-red-600';
        } else {
          textColorClass = value >= column.threshold ? 'text-green-600' : 'text-red-600';
        }
      }
      
      // Adicionar ícone de tendência se specificado
      const icon = value > 0 
        ? <ArrowUp size={12} className="ml-1" />
        : value < 0 
          ? <ArrowDown size={12} className="ml-1" />
          : <Minus size={12} className="ml-1" />;
      
      cellContent = (
        <span className={`font-medium ${textColorClass}`}>
          {value !== null && value !== undefined ? value : '-'}
          {column.showTrendIcon && icon}
        </span>
      );
    } else if (column.type === 'date') {
      cellContent = value 
        ? new Date(value).toLocaleDateString('pt-BR')
        : '-';
    } else if (column.type === 'boolean') {
      cellContent = value 
        ? <span className="text-green-600">Sim</span>
        : <span className="text-red-600">Não</span>;
    } else if (column.type === 'tag' || column.type === 'badge') {
      // Obter cor baseada no valor
      let badgeColorClass = 'bg-gray-100 text-gray-800';
      
      if (column.tagColors && column.tagColors[value]) {
        badgeColorClass = column.tagColors[value];
      } else if (column.colorMapping && typeof column.colorMapping === 'function') {
        badgeColorClass = column.colorMapping(value, row);
      }
      
      cellContent = value ? (
        <span className={`px-2 py-1 text-xs rounded-full ${badgeColorClass}`}>
          {value}
        </span>
      ) : '-';
    } else if (column.type === 'progress') {
      // Progresso com barra
      const percent = parseFloat(value) || 0;
      
      // Cores baseadas no progresso
      let colorClass = 'bg-blue-500';
      if (percent >= 90) colorClass = 'bg-green-500';
      else if (percent >= 60) colorClass = 'bg-blue-500';
      else if (percent >= 30) colorClass = 'bg-yellow-500';
      else colorClass = 'bg-red-500';
      
      cellContent = (
        <div className="flex items-center">
          <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2">
            <div 
              className={`h-2.5 rounded-full ${colorClass}`} 
              style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
            />
          </div>
          <span className="text-xs font-medium">{percent}%</span>
        </div>
      );
    } else {
      // Texto padrão
      cellContent = value !== null && value !== undefined ? value : '-';
    }
    
    return (
      <td 
        key={column.key} 
        className={`px-4 py-3 ${column.cellClassName || ''}`}
      >
        {cellContent}
      </td>
    );
  };
  
  // Renderizar linha expandida
  const renderExpandedRow = (row) => {
    // Verificar se existem detalhes expandidos
    if (!row._expanded && !row.details) return null;
    
    const expandedContent = row._expanded ? row._expanded : row.details;
    
    return (
      <tr>
        <td colSpan={columns.filter(col => !col.hidden).length + (enableSelection ? 1 : 0) + (actions.length ? 1 : 0)}>
          <div className="p-4 bg-gray-50 border-t border-gray-200">
            {typeof expandedContent === 'function' 
              ? expandedContent(row)
              : expandedContent}
          </div>
        </td>
      </tr>
    );
  };
  
  // Renderizar área de filtros
  const renderFilterSection = () => {
    if (!enableFiltering || !visibleFilters) return null;
    
    const filterableColumns = columns.filter(col => 
      !col.hidden && col.filterable !== false
    );
    
    if (!filterableColumns.length) return null;
    
    return (
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filterableColumns.map(column => (
            <div key={`filter-${column.key}`}>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {column.label}
              </label>
              
              {/* Tipo de filtro baseado na configuração da coluna */}
              {column.filterType === 'select' && column.filterOptions ? (
                <select
                  value={filters[column.key] || 'all'}
                  onChange={(e) => handleFilter(column.key, e.target.value)}
                  className="form-select text-sm w-full"
                >
                  <option value="all">Todos</option>
                  {column.filterOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : column.filterType === 'range' ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    placeholder="Min"
                    className="form-input text-sm w-full"
                    value={(filters[column.key] || [])[0] || ''}
                    onChange={(e) => {
                      const min = e.target.value;
                      const max = (filters[column.key] || [])[1] || '';
                      handleFilter(column.key, [min, max]);
                    }}
                  />
                  <span>-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    className="form-input text-sm w-full"
                    value={(filters[column.key] || [])[1] || ''}
                    onChange={(e) => {
                      const min = (filters[column.key] || [])[0] || '';
                      const max = e.target.value;
                      handleFilter(column.key, [min, max]);
                    }}
                  />
                </div>
              ) : (
                <input
                  type="text"
                  placeholder={`Filtrar por ${column.label}`}
                  className="form-input text-sm w-full"
                  value={filters[column.key] || ''}
                  onChange={(e) => handleFilter(column.key, e.target.value)}
                />
              )}
            </div>
          ))}
        </div>
        
        {/* Botões de ação para filtros */}
        <div className="flex justify-end mt-4">
          <button
            type="button"
            className="text-sm text-gray-600 hover:text-gray-800 mr-4"
            onClick={() => {
              setFilters({});
              setCurrentPage(1);
            }}
          >
            Limpar filtros
          </button>
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={() => setVisibleFilters(false)}
          >
            Aplicar filtros
          </button>
        </div>
      </div>
    );
  };
  
  // Renderizar barra de paginação
  const renderPagination = () => {
    if (!pagination.enabled || totalPages <= 1) return null;
    
    // Determinar quais páginas mostrar
    const visiblePages = [];
    const maxPagesToShow = 5;
    
    // Sempre mostrar a primeira página, a atual, e a última
    if (totalPages <= maxPagesToShow) {
      // Mostrar todas as páginas
      for (let i = 1; i <= totalPages; i++) {
        visiblePages.push(i);
      }
    } else {
      // Mostrar um subconjunto de páginas
      visiblePages.push(1); // Primeira página
      
      // Páginas ao redor da atual
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      if (start > 2) {
        visiblePages.push('...');
      }
      
      for (let i = start; i <= end; i++) {
        visiblePages.push(i);
      }
      
      if (end < totalPages - 1) {
        visiblePages.push('...');
      }
      
      visiblePages.push(totalPages); // Última página
    }
    
    return (
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-600">
          Exibindo {processedData.length} de {data.length} registros
        </div>
        
        <div className="flex items-center space-x-1">
          <button
            type="button"
            className="px-3 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            Anterior
          </button>
          
          {visiblePages.map((page, index) => (
            <button
              key={`page-${index}`}
              type="button"
              className={`px-3 py-1 rounded border ${
                page === currentPage
                  ? 'border-blue-500 bg-blue-50 text-blue-600'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-100'
              } ${page === '...' ? 'cursor-default' : ''}`}
              disabled={page === '...'}
              onClick={() => page !== '...' && handlePageChange(page)}
            >
              {page}
            </button>
          ))}
          
          <button
            type="button"
            className="px-3 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            Próxima
          </button>
        </div>
      </div>
    );
  };
  
  // Criar mapa de IDs para rápido acesso ao verificar seleção
  const selectedRowsMap = useMemo(() => {
    const map = {};
    localSelectedRows.forEach(id => {
      map[id] = true;
    });
    return map;
  }, [localSelectedRows]);
  
  // Verificar estado de "selecionar todos"
  const allSelected = processedData.length > 0 && 
    processedData.every(row => selectedRowsMap[row[idField]]);
  
  // Verificar se há algum item selecionado
  const someSelected = processedData.some(row => selectedRowsMap[row[idField]]);
  
  // Renderizar cabeçalho da tabela com título e ações
  const renderTableHeader = () => {
    return (
      <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-4">
        {/* Título */}
        <h3 className="text-lg font-semibold text-gray-800">
          {title}
        </h3>
        
        {/* Ações da tabela */}
        <div className="flex items-center space-x-3">
          {/* Campo de busca */}
          {enableFiltering && (
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar..."
                className="form-input text-sm pl-9 pr-4 py-2"
                value={searchTerm}
                onChange={handleSearch}
              />
              <Search 
                size={18} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
              />
            </div>
          )}
          
          {/* Botão de filtro */}
          {enableFiltering && (
            <button
              type="button"
              className={`p-2 rounded-lg ${
                visibleFilters || Object.keys(filters).length > 0
                  ? 'bg-blue-50 text-blue-600'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              onClick={() => setVisibleFilters(!visibleFilters)}
            >
              <Filter size={18} />
            </button>
          )}
          
          {/* Botão de configurações */}
          <button
            type="button"
            className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
          >
            <SlidersHorizontal size={18} />
          </button>
          
          {/* Botão de download */}
          <button
            type="button"
            className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
          >
            <Download size={18} />
          </button>
        </div>
      </div>
    );
  };
  
  return (
    <div className="overflow-hidden bg-white rounded-xl shadow-md border border-gray-200">
      {/* Cabeçalho da tabela */}
      {renderTableHeader()}
      
      {/* Área de filtros expandível */}
      {renderFilterSection()}
      
      {/* Ações em lote quando itens selecionados */}
      {enableSelection && someSelected && (
        <div className="px-6 py-3 bg-blue-50 border-b border-blue-200 flex items-center justify-between">
          <div className="text-sm text-blue-700">
            {localSelectedRows.length} {localSelectedRows.length === 1 ? 'item selecionado' : 'itens selecionados'}
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              type="button"
              className="text-sm text-blue-600 hover:text-blue-800"
              onClick={() => setLocalSelectedRows([])}
            >
              Limpar seleção
            </button>
            
            <button
              type="button"
              className="btn-danger text-sm"
            >
              <Trash size={16} className="mr-1" />
              Excluir selecionados
            </button>
          </div>
        </div>
      )}
      
      {/* Tabela */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
            <tr>
              {/* Coluna de seleção */}
              {enableSelection && (
                <th className="w-10 px-4 py-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={allSelected}
                      onChange={handleSelectAll}
                    />
                  </div>
                </th>
              )}
              
              {/* Colunas de dados */}
              {columns.map(renderColumnHeader)}
              
              {/* Coluna de ações */}
              {actions.length > 0 && (
                <th className="px-4 py-3 text-right">Ações</th>
              )}
            </tr>
          </thead>
          
          <tbody className="bg-white divide-y divide-gray-200">
            {processedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.filter(col => !col.hidden).length + (enableSelection ? 1 : 0) + (actions.length ? 1 : 0)}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  Nenhum registro encontrado.
                </td>
              </tr>
            ) : (
              processedData.map((row, rowIndex) => {
                // Verificar se esta linha está expandida
                const isExpanded = expandedRows[row[idField]];
                
                // Verificar se esta linha está selecionada
                const isSelected = selectedRowsMap[row[idField]];
                
                // Aplicar estilos condicionais
                let rowClasses = '';
                
                if (conditionalStyles) {
                  Object.keys(conditionalStyles).forEach(key => {
                    const condition = conditionalStyles[key].condition;
                    const className = conditionalStyles[key].className;
                    
                    if (typeof condition === 'function' && condition(row)) {
                      rowClasses += ` ${className}`;
                    }
                  });
                }
                
                // Adicionar classe para linha selecionada
                if (isSelected) {
                  rowClasses += ' bg-blue-50';
                }
                
                // Adicionar classe para linha com hover
                rowClasses += ' hover:bg-gray-50';
                
                return (
                  <React.Fragment key={`row-${row[idField] || rowIndex}`}>
                    <tr className={rowClasses.trim()}>
                      {/* Coluna de seleção */}
                      {enableSelection && (
                        <td className="w-10 px-4 py-3">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              checked={isSelected}
                              onChange={() => handleRowSelection(row[idField])}
                            />
                          </div>
                        </td>
                      )}
                      
                      {/* Células de dados */}
                      {columns.map(column => renderCell(row, column))}
                      
                      {/* Coluna de ações */}
                      {actions.length > 0 && (
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end space-x-2">
                            {actions.map((action, actionIndex) => (
                              <button
                                key={`action-${actionIndex}`}
                                type="button"
                                className={`p-1 rounded-full ${action.className || 'text-gray-600 hover:bg-gray-100'}`}
                                onClick={() => action.onClick && action.onClick(row)}
                                title={action.tooltip || action.label}
                              >
                                {action.icon || <Eye size={16} />}
                              </button>
                            ))}
                            
                            {/* Botão para expandir/colapsar linha */}
                            {row._expanded || row.details ? (
                              <button
                                type="button"
                                className="p-1 rounded-full text-gray-600 hover:bg-gray-100"
                                onClick={() => toggleRowExpansion(row[idField])}
                              >
                                {isExpanded ? (
                                  <ChevronUp size={16} />
                                ) : (
                                  <ChevronDown size={16} />
                                )}
                              </button>
                            ) : null}
                          </div>
                        </td>
                      )}
                    </tr>
                    
                    {/* Linha expandida */}
                    {isExpanded && renderExpandedRow(row)}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      
      {/* Paginação */}
      <div className="px-6 py-4 border-t border-gray-200">
        {renderPagination()}
      </div>
    </div>
  );
};

export default MetricTable;
